import { Tip } from "@/constants/tip";
import { getTipContentPath } from "../../../../utils/guidebook/getGuidebookContentPath";
import { Box } from "@chakra-ui/react";
import { getArticlesSearch } from "../../remotes/getArticleSearch";
import { useState } from "react";
import { useInfiniteQuery } from "react-query";
import { SearchResultCount } from "../SearchResultCount";
import { customEvent, HStack, Tags, useResponsive, VStack } from "goi_common";
import { GuidebookThumbnail } from "./GuidebookThumbnail";
import { useInifiniteDatas } from "@/hooks/useInfiniteDatas";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { NoResult } from "../NoResult";
import { TAGS } from "@/constants/guidebookTags";
import { ArticleSummary } from "pages/guidebook";
import { css } from "@emotion/react";

interface GuidebookThumbnailsProps {
  keyword?: string;
  articles?: ArticleSummary[];
}

export function GuidebookThumbnails({ keyword, articles }: GuidebookThumbnailsProps) {
  const [tag, setTag] = useState<string>("before");

  const {
    data: articleSearchPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(["articles-search", keyword], getArticlesSearch(keyword), {
    enabled: keyword != null,
    getNextPageParam: (lastPage) => lastPage.next,
    refetchOnMount: false,
  });
  const { datas: articlesSearch, count } = useInifiniteDatas(articleSearchPages);
  const { setTarget } = useInfiniteScroll(fetchNextPage);

  const { setResponsive } = useResponsive();
  return (
    <Box marginTop="24px">
      {keyword ? (
        <SearchResultCount count={count} />
      ) : (
        <Tags
          tags={TAGS}
          value={tag}
          onChange={(v) => {
            customEvent({
              newGtm: {
                path: "guidebook",
                trigger_and_target: `chip_guidebook-${
                  tag as "before" | "ongoing" | "after" | "etc"
                }`,
              },
            });
            setTag(v);
          }}
          padding={24}
        />
      )}
      {keyword ? (
        <HStack gap={16} css={thumbnailsRootCss}>
          {articlesSearch?.map((article) => {
            return (
              <VStack
                key={article.title}
                width={setResponsive("100%", "calc(50% - 8px)", "calc(33.33% - 12px)")}
              >
                <GuidebookThumbnail
                  item={article as unknown as Tip}
                  category={article.category}
                  tags={TAGS}
                  id={article.index}
                  to={getTipContentPath(article.category, article.index)}
                  keyword={keyword}
                  onClick={() => {
                    let stage = article.category;
                    if (stage.includes("ongoing")) {
                      stage = "ongoing";
                    }

                    customEvent({
                      newGtm: {
                        path: "guidebook_search",
                        trigger_and_target: `content_detail-uv_guidebook_tip_${
                          stage as "before" | "ongoing" | "after" | "etc"
                        }`,
                        text: article.title,
                      },
                    });
                  }}
                />
              </VStack>
            );
          })}
          {keyword && articlesSearch?.length === 0 && (
            <VStack alignItems="center" width="100%">
              <NoResult keyword={keyword} />
            </VStack>
          )}
          {articlesSearch && hasNextPage && !isFetchingNextPage && <div ref={setTarget} />}
        </HStack>
      ) : (
        <HStack gap={16} css={thumbnailsRootCss}>
          {articles
            ?.filter((a) => a.category.includes(tag))
            .map((item: any) => {
              const slug = `${item.category}-${item.index}`;

              return (
                <VStack
                  key={slug}
                  width={setResponsive("100%", "calc(50% - 8px)", "calc(33.33% - 12px)")}
                >
                  <GuidebookThumbnail
                    key={item.title}
                    item={item}
                    category={item.category}
                    tags={TAGS}
                    id={item.index}
                    to={getTipContentPath(item.category, item.index)}
                    onClick={() => {
                      let stage = item.category;
                      if (stage.includes("ongoing")) {
                        stage = "ongoing";
                      }

                      customEvent({
                        newGtm: {
                          path: "guidebook",
                          trigger_and_target: `content_detail-uv_guidebook_tip_${
                            stage as "before" | "ongoing" | "after" | "etc"
                          }`,
                          text: item.title,
                        },
                      });
                    }}
                  />
                </VStack>
              );
            })}
        </HStack>
      )}
    </Box>
  );
}

const thumbnailsRootCss = css({
  padding: "24px",
  flexWrap: "wrap",
});
